import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SingleDropDown from "../../SingleDropDown";
import { FormControl, TextField } from "@material-ui/core";
import MultiDropDownObjects from "../../MultiDropDownObjects";
import SingleDropDownOfficer from "../../SingleDropDownOfficer";
import DrugsInvolvedObjects from "../../DrugsInvolvedObjects";
import CheckboxWithLabel from "../../CheckboxWithLabel";
import { INCIDENT_ACTIONS } from "../../../Actions/IncidentActions";
import { IncidentsContext } from "../../../Contexts/IncidentsContext";
import { SetupContext } from "../../../Contexts/SetupContext";
import { MutationContext } from "../../../Contexts/MutationContext";
import { localGetData, parseObjectArrayIdString } from "../../../utils/utils";
import { useStyles } from "../../../Styles/styles";
import { UserContext } from "../../../Contexts/UserContext";
import { SingleIncidentContext } from "../../../Contexts/SingleIncidentContext";
import { shouldBeLocked } from "../../../utils/Roles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import _ from "lodash";
import { getPoldTrigger } from "../../../utils/getPoldTrigger";

const stateToMutation = {
  bac: "bac",
  incidentInvolvement: "incidentInvolvementId",
  victimType: "victimTypeId",
  clothing: "clothing",
  computerUse: "computerUse",
  docStatus: "docStatusId",
  drugTypes: "drugTypeIds",
  ibrResidentType: "ibrResidentTypeId",
  injuryTypes: "injuryTypeIds",
  opiateBlocker: "opiateBlocker",
  orphanage: "orphanage",
  paperwork: "paperworkId",
  statementTakenBy: "statementTakenById",
  statementType: "statementTypeId",
  weaponType: "weaponTypeId",
  sealed: "sealed",
  aliasName: "aliasName",
  buildType: "buildTypeId",
  cell: "cell",
  deceased: "deceased",
  distinguishingCharacteristics: "distinguishingCharacteristics",
  driversLicense: "driversLicense",
  email: "email",
  employer: "employer",
  employerAddress: "employerAddressId",
  eyeColor: "eyeColorId",
  facialHairType: "facialHairTypeId",
  fbiNumber: "fbiNumber",
  firstName: "firstName",
  fishWildlifeLicense: "fishWildlifeLicense",
  gender: "genderId",
  hairColor: "hairColorId",
  hairLength: "hairLengthId",
  height: "height",
  homeAddress: "homeAddressId",
  ibrArresteeRaceType: "ibrArresteeRaceTypeId",
  lastName: "lastName",
  licenseState: "licenseStateId",
  mailingAddress: "mailingAddressId",
  mentalHealthAlert: "mentalHealthAlert",
  middleName: "middleName",
  nibrsGender: "nibrsGenderId",
  notes: "notes",
  organization: "organization",
  phone: "phone",
  pobAddress: "pobAddressId",
  race: "raceId",
  residentType: "residentTypeId",
  sid: "sid",
  substanceAbuseAlert: "substanceAbuseAlert",
  suffix: "suffix",
  violenceAlert: "violenceAlert",
  waiverFine: "waiverFine",
  weaponsAlert: "weaponsAlert",
  weight: "weight",
  work: "work",
  dob: "dob",
  gangAffiliateds: "gangAffiliatedsIds",
  groups: "groupIds",
  school: "school",
  juvenile: "juvenile",
  yoe: "yoe",
};

const PersonIncidentSpecific = ({
  user,
  pis,
  incidentId,
  incidentUuid,
  incidentIndex,
  incidentOriId,
  personIndex,
  personTab,
  setPersonTab,
  userRoles,
  disabled,
  incidentCallTime,
  setPopper,
  ...props
}) => {
  const classes = useStyles();
  const { setup } = useContext(SetupContext);
  const { incidentDispatch, incidents } = useContext(IncidentsContext);
  const { allOfficers } = useContext(UserContext);
  const { addPis } = useContext(MutationContext);
  const { yoeJuvenileStatuses } = useContext(SingleIncidentContext);
  const yoeEnabled = useMemo(() => {
    const res = yoeJuvenileStatuses?.find(pos => pos?.pisId === pis?.id);
    return res?.isYoeEligible;
  }, [yoeJuvenileStatuses, pis?.id]);

  const incident = useMemo(() => {
    return (
      incidents.find(i => i.id === incidentId || i.uuid === incidentUuid) ||
      null
    );
  }, [incidents, incidentId, incidentUuid]);

  const poldOptOut = useMemo(() => {
    return incident?.ori?.poldOptOut || false;
  }, [incident]);

  const personOffenseStatus = useMemo(() => {
    const res = yoeJuvenileStatuses?.find(pos => pos?.pisId === pis?.id);

    return {
      isJuvenile: res?.isJuvenile,
      isYoeEligible: res?.isYoeEligible,
    };
  }, [yoeJuvenileStatuses, pis?.id]);

  const { UPDATE_PIS } = INCIDENT_ACTIONS;
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles]
  );

  const {
    setupLists: {
      victimTypes,
      docStatuses,
      weaponTypes,
      injuryTypes: injuryTypess,
      residentTypes,
      statementTypes,
      drugTypes: drugTypess,
      drugClassifications,
    },
  } = setup;

  const localStoreSpecificSetup =
    localGetData(`specific-setup-${incidentOriId}`) || {};
  const { setupLists: specificSetupLists } = localStoreSpecificSetup || {};
  const { incidentInvolvements, paperworks } = specificSetupLists || {};

  const [labelWidth, setLabelWidth] = useState(0);
  const inputLabel = useRef(null);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, [inputLabel]);

  const handlePisChange = useCallback(
    (event, submit = true) => {
      let name;
      let value;
      if (event.target.type === "checkbox") {
        value = event.target.checked;
        name = event.target.value;
      } else {
        value = event.target.value;
        name = event.target.name;
      }
      let fieldToUpdate = stateToMutation[name] || name;

      if (!name || !fieldToUpdate) {
        alert(
          `Please add a name field that matches the state name to the component that was just updated`
        );
        return null;
      }
      let mutationValue =
        value && Object.prototype.hasOwnProperty.call(value, "id")
          ? value.id
          : value;

      if (Array.isArray(value)) {
        mutationValue = parseObjectArrayIdString(value);
      }
      if (name === "drugTypes" && Array.isArray(value)) {
        const alcoholInvolved = value.find(dt => dt.name === "Alcohol");
        if (alcoholInvolved) {
          const { needsPoldWarning } = getPoldTrigger({
            incident,
            poldOptOut,
            forceAlcohol: true,
          });
          if (needsPoldWarning && setPopper) {
            setPopper({
              open: true,
              severity: "info",
              content:
                "Please fill out the POLD field in Initial Call Information.",
            });
          }
        }
      }
      submit &&
        addPis({
          variables: {
            input: {
              originatorId: user?.id,
              incidentId: incidentId ? incidentId : incidentUuid,
              personIncidentSpecificId: pis.id,
              [fieldToUpdate]: mutationValue,
            },
          },
        });
      incidentDispatch({
        type: UPDATE_PIS,
        payload: {
          incidentId: incidentId ? incidentId : incidentUuid,
          pisId: pis.id,
          pisUuid: pis?.uuid,
          property: name,
          value: value,
        },
      });
    },
    [
      UPDATE_PIS,
      addPis,
      incidentDispatch,
      incidentId,
      incidentUuid,
      pis.id,
      pis?.uuid,
      user?.id,
      incident,
      poldOptOut,
      setPopper,
    ]
  );

  const selectedWeaponType = useMemo(() => {
    if (!pis?.weaponType || !weaponTypes?.length) return null;
    return (
      weaponTypes.find(
        option => String(option?.id) === String(pis?.weaponType?.id)
      ) || null
    );
  }, [pis?.weaponType, weaponTypes]);

  const handleWeaponTypeChange = useCallback(
    value => {
      handlePisChange(
        {
          target: {
            name: "weaponType",
            value: value || null,
          },
        },
        true
      );
    },
    [handlePisChange]
  );

  return (
    <div style={{ margin: "8px" }}>
      <h3>Incident Specifics</h3>
      <SingleDropDown
        handleChange={handlePisChange}
        stateName={"incidentInvolvement"}
        value={pis?.incidentInvolvement}
        options={incidentInvolvements}
        title={"Person Type"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <SingleDropDown
        handleChange={handlePisChange}
        stateName={"victimType"}
        value={pis?.victimType}
        // value={""}
        options={victimTypes}
        title={"Victim Type"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <TextField
        data-cy="ClothingDescription"
        inputProps={{ "data-cy": "clothing-input" }}
        InputLabelProps={{ shrink: true }}
        id="clothing-id"
        label="Clothing Description"
        value={pis?.clothing || ""}
        name={"clothing"}
        onChange={e => handlePisChange(e, false)}
        onBlur={e => handlePisChange(e, true)}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.target.blur();
          }
        }}
        className={classes.formControl}
        disabled={disabled}
      />
      <SingleDropDown
        handleChange={handlePisChange}
        stateName={"docStatus"}
        value={pis?.docStatus}
        options={docStatuses}
        title={"Dept. of Corrections Status"}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <FormControl className={classes.formControl}>
        <Autocomplete
          id="ibr-weapon-type-autocomplete"
          size="small"
          disabled={memoizedLocked}
          options={weaponTypes || []}
          getOptionLabel={option => option?.name || ""}
          value={selectedWeaponType}
          onChange={(e, value) => handleWeaponTypeChange(value)}
          renderInput={params => (
            <TextField
              {...params}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                ...params.InputProps,
                style: {
                  ...(params.InputProps?.style || {}),
                  minWidth: 250,
                  width: "100%",
                  whiteSpace: "nowrap",
                  overflow: "visible",
                },
              }}
              inputProps={{
                ...params.inputProps,
                style: {
                  ...(params.inputProps?.style || {}),
                  whiteSpace: "nowrap",
                  overflow: "visible",
                  textOverflow: "clip",
                },
              }}
              variant="standard"
              label="IBR&nbsp;Weapon&nbsp;Type"
            />
          )}
        />
      </FormControl>
      <MultiDropDownObjects
        id={"ibrInjury"}
        title={"IBR Injury."}
        stateName={"injuryTypes"}
        handleChange={handlePisChange}
        selectedValues={
          pis?.injuryTypes
            ? injuryTypess.filter(option =>
                pis?.injuryTypes.some(i => option.id === i.id)
              )
            : []
        }
        options={injuryTypess}
        formStyle={classes.formControl}
        labelStyle={classes.multiInputLabel}
        selectStyle={null}
        chipsStyle={classes.chips}
        chipStyle={classes.chip}
        userRoles={userRoles}
        {...props}
      />
      <SingleDropDown
        stateName={"paperwork"}
        value={pis?.paperwork}
        options={paperworks}
        title={"Paperwork"}
        handleChange={handlePisChange}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <CheckboxWithLabel
        checked={
          _.isBoolean(personOffenseStatus?.isJuvenile)
            ? personOffenseStatus?.isJuvenile
            : pis?.juvenile
        }
        stateName={"juvenile"}
        title={"Juv."}
        onChange={e => handlePisChange(e, true)}
        formStyle={classes.formControl}
        userRoles={userRoles}
        disabled={true}
        {...props}
      />
      <CheckboxWithLabel
        checked={
          _.isBoolean(personOffenseStatus?.isYoeEligible)
            ? personOffenseStatus?.isYoeEligible && pis?.yoe
            : false
        }
        stateName={"yoe"}
        title={"YOE"}
        onChange={e => handlePisChange(e, true)}
        formStyle={classes.formControl}
        userRoles={userRoles}
        disabled={!yoeEnabled}
        {...props}
      />
      <SingleDropDown
        stateName={"ibrResidentType"}
        value={pis?.ibrResidentType}
        options={residentTypes}
        title={"IBR Res. Type"}
        handleChange={handlePisChange}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <SingleDropDown
        stateName={"statementType"}
        value={pis?.statementType}
        options={statementTypes}
        title={"Stmt Type"}
        handleChange={handlePisChange}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      <SingleDropDownOfficer
        stateName={"statementTakenBy"}
        value={pis?.statementTakenBy || null}
        options={allOfficers}
        title={"Stmt Taken By"}
        handleChange={handlePisChange}
        classStyle={classes.formControl}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        userRoles={userRoles}
        {...props}
      />
      {/* <div>Placeholder for Statement Taken By</div> */}
      <TextField
        data-cy="BAC"
        inputProps={{ "data-cy": "bac-input" }}
        InputLabelProps={{ shrink: true }}
        id="bac-id"
        label="BAC"
        value={pis?.bac || ""}
        name={"bac"}
        onChange={e => handlePisChange(e, false)}
        onBlur={e => handlePisChange(e, true)}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.target.blur();
          }
        }}
        className={classes.formControl}
        disabled={disabled}
      />
      <DrugsInvolvedObjects
        id={pis?.id}
        title={"Drugs Involved"}
        stateName={"drugTypes"}
        selectedValues={
          pis?.drugTypes
            ? drugTypess.filter(dt => pis.drugTypes.some(d => dt.id === d.id))
            : []
        }
        onChange={handlePisChange}
        options={drugTypess}
        categories={drugClassifications}
        formStyle={classes.formControl}
        labelStyle={classes.multiInputLabel}
        chipsStyle={null}
        chipStyle={null}
        userRoles={userRoles}
        {...props}
      />
      <CheckboxWithLabel
        checked={pis?.opiateBlocker || false}
        stateName={"opiateBlocker"}
        title={"Opiate Blocker"}
        onChange={e => handlePisChange(e, true)}
        formStyle={classes.formControl}
        userRoles={userRoles}
        {...props}
      />
      <CheckboxWithLabel
        checked={pis?.computerUse || false}
        stateName={"computerUse"}
        title={"Using Computer/Hand-held"}
        onChange={e => handlePisChange(e, true)}
        formStyle={classes.formControl}
        userRoles={userRoles}
        {...props}
      />
      <CheckboxWithLabel
        checked={pis?.orphanage || false}
        stateName={"orphanage"}
        title={"Orphanage"}
        onChange={e => handlePisChange(e, true)}
        formStyle={classes.formControl}
        userRoles={userRoles}
        {...props}
      />
    </div>
  );
};

export default PersonIncidentSpecific;
